import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDate } from "../../utils/TimeAndDateConversions";
import fetchFloorJournals from "../../utils/fetchFloorJournals";
import {
  MDBRow,
  MDBCol,
  MDBInputGroup,
  MDBInput,
  MDBBtn,
  MDBIcon,
  MDBSpinner,
} from "mdb-react-ui-kit";

const Journals = ({ legislativeCalendarDays, committeeData }) => {
  const [customRowData, setCustomRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: "contains" },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!legislativeCalendarDays || legislativeCalendarDays.length === 0) {
          setLoading(false);
          return;
        }

        // Get current date for filtering future Agendas
        const today = new Date();
        const options = { timeZone: "America/Denver" };
        const usDate = new Date(today.toLocaleString("en-US", options));
        usDate.setHours(23, 59, 59, 999);
        let allRows = [];

        // Legilstive day 0 is coming from the API, so we need to filter it out!
        const filteredCalendarDays = legislativeCalendarDays.filter(
          (day) => day.calendarDay !== 0
        );

        for (const day of filteredCalendarDays) {
          const journalLink = await fetchFloorJournals(
            day.session.legislature.ordinals,
            "STANDING",
            "ST",
            committeeData.committeeDetails.committeeCode.code,
            committeeData.chamber,
            day.calendarDate
          );

          const dayDateTime = `${day.calendarDate}T00:00:00`;
          const dayDate = new Date(dayDateTime);
          const etDayDate = new Date(dayDate.toLocaleString("en-US", options));
          if (etDayDate <= usDate) {
            allRows.push({
              ...day,
              committeeData: committeeData,
              journalLink: journalLink,
              hasJournalLink: !!journalLink,
              formattedCalendarDate: formatDate(dayDateTime),
              fullDateTime: new Date(dayDateTime),
              id: `day-${day.id}`,
            });
          }
        }
        setCustomRowData(allRows);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [legislativeCalendarDays, committeeData]);

  const legislativeDayTemplate = (rowData) => {
    if (!rowData) return null;
    return <>Day {rowData.calendarDay}</>;
  };

  const dateTimeTemplate = (rowData) => {
    if (!rowData || !rowData.formattedCalendarDate) return null;

    if (rowData.conveneTime) {
      const time = new Date(rowData.conveneTime)
        .toLocaleTimeString("en-US", {
          hour: "numeric", // removes the leading zero
          minute: "2-digit",
          hour12: true,
        })
        .replace(/ (\w{2})$/, "$1"); // removes the space before AM/PM
      return `${rowData.formattedCalendarDate} ${time}`;
    }

    return rowData.formattedCalendarDate;
  };

  const journalsTemplate = (rowData) => {
    if (!rowData || !rowData.journalLink) return null;

    return (
      <MDBBtn
        color="link"
        rippleColor="light"
        onClick={(e) => {
          e.preventDefault();
          window.open(rowData.journalLink, "_blank");
        }}
      >
        <MDBIcon far icon="file-alt" /> Journal
      </MDBBtn>
    );
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  return (
    <>
      <MDBRow className="g-1">
        <MDBCol size="lg">
          <MDBInputGroup
            className="mb-3"
            noBorder
            textBefore={<MDBIcon fas icon="search" />}
          >
            <MDBInput
              type="text"
              label="Search Meetings"
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
            />
          </MDBInputGroup>
        </MDBCol>
      </MDBRow>
      <DataTable
        value={customRowData}
        dataKey="id"
        showGridlines
        stripedRows
        resizableColumns
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={100}
        rowsPerPageOptions={[25, 50, 100]}
        scrollable
        scrollHeight="580px"
        sortField="fullDateTime"
        sortOrder={-1}
        removableSort
        filters={filters}
        globalFilterFields={["calendarDay", "formattedCalendarDate"]}
        loading={loading}
        emptyMessage="No meetings found"
      >
        <Column
          header="Legislative Day"
          field="calendarDay"
          body={legislativeDayTemplate}
          sortable
        />
        <Column
          header="Date & Time"
          field="fullDateTime"
          body={dateTimeTemplate}
          sortable
        />
        <Column
          header="Journals"
          field="hasJournalLink"
          body={journalsTemplate}
          sortable
        />
      </DataTable>
    </>
  );
};

export default Journals;
